import io from "socket.io-client";
import { logger } from "@/helpers/tslog";
import { apiBase, getDefaultSocketConfig } from "@/utils/apiClient";
import {
  JOIN_SLIDE_GENERATION_ROOM_EVENT,
  SLIDE_GENERATION_PROGRESS_EVENT,
  type SlidesProgressSnapshot,
} from "@/utils/slides/slidesTaskpaneContract";

type SnapshotListener = (snapshot: SlidesProgressSnapshot) => void;

class SlidesProgressSocketSingleton {
  private socket: ReturnType<typeof io> | null = null;
  private listenersByJob = new Map<string, Set<SnapshotListener>>();
  private joinedRooms = new Set<string>();

  subscribe(jobId: string, listener: SnapshotListener): () => void {
    let set = this.listenersByJob.get(jobId);
    if (!set) {
      set = new Set();
      this.listenersByJob.set(jobId, set);
    }
    set.add(listener);
    this.ensureConnected();
    this.joinRoomIfConnected(jobId);
    return () => this.unsubscribe(jobId, listener);
  }

  private unsubscribe(jobId: string, listener: SnapshotListener): void {
    const set = this.listenersByJob.get(jobId);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) {
      this.listenersByJob.delete(jobId);
      this.joinedRooms.delete(jobId);
    }
    if (this.listenersByJob.size === 0) {
      this.socket?.disconnect();
      this.socket = null;
      this.joinedRooms.clear();
    }
  }

  private ensureConnected(): void {
    if (this.socket) return;
    const base = apiBase();
    if (!base) return;
    const socket = io(base, getDefaultSocketConfig());
    this.socket = socket;
    socket.on("connect", () => {
      for (const jobId of Array.from(this.listenersByJob.keys())) {
        socket.emit(JOIN_SLIDE_GENERATION_ROOM_EVENT, jobId);
        this.joinedRooms.add(jobId);
      }
    });
    socket.on(SLIDE_GENERATION_PROGRESS_EVENT, (snapshot: SlidesProgressSnapshot) => {
      const listeners = snapshot?.jobId ? this.listenersByJob.get(snapshot.jobId) : undefined;
      if (!listeners) return;
      for (const listener of Array.from(listeners)) listener(snapshot);
    });
    socket.on("connect_error", (error: Error) => {
      logger.warn("[SlidesProgressSocket] connect error", error);
    });
  }

  private joinRoomIfConnected(jobId: string): void {
    if (!this.socket?.connected) return;
    if (this.joinedRooms.has(jobId)) return;
    this.socket.emit(JOIN_SLIDE_GENERATION_ROOM_EVENT, jobId);
    this.joinedRooms.add(jobId);
  }
}

export const slidesProgressSocket = new SlidesProgressSocketSingleton();
